import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Container, ContainerType, Row, Column } from '@as/cotopaxi';
import { ErrorMessage, CookieConsentSettingState, AuthFormLocation, AuthStatusCode, RegistrationSearchParameters, } from '@as-react/common-types';
import { getHomepageUrl } from '@as-react/common-utils';
import { Box, Divider, TextAlpha, useMediaQueryMatchesOnMobile } from '@yonderland/andes-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Authentication } from '../components/account/authentication/Authentication';
import { LoginRegistrationForm } from '../components/account/registration/LoginRegistrationForm';
import { ReturnPortalGuestLogin, } from '../components/account/return-portal-guest-login/ReturnPortalGuestLogin';
import { LOGIN_FORM_ERROR_FEEDBACK_ID, RETURN_PORTAL_GUEST_FORM_ERROR_FEEDBACK_ID, } from '../constants/DOMElementsConstants';
import { I18N_NAMESPACES } from '../constants/i18nConstants';
import { ReturnPortalLoginType } from '../enums/AnalyticsEnums';
import { analyticsActions } from '../redux/analytics/actions/AnalyticsActions.creators';
import { authActions } from '../redux/authentication/actions/AuthActions.creators';
import { makeGetLoginError, makeGetResetPasswordState, makeGetLoginLoading, makeGetErrorReturnGuestState, makeGetLoginReturnGuestLoading, makeGetLoggedInReturnGuestState, } from '../redux/authentication/selectors/AuthSelectors';
import { makeGetCookieConsentSettingAvailable } from '../redux/cookieConsent/selectors/CookieConsentSelector';
import { makeGetLoggedInState, makeGetSessionState } from '../redux/session/selectors/SessionSelector';
import { ScrollUtil } from '../util/ScrollUtil';
const ReturnPortalLoginContainer = ({ returnItemsPath, orderOverviewPath, accountRegistrationPath, enableLoginForm, loginForm: loginFormProps, }) => {
    const isMobile = useMediaQueryMatchesOnMobile();
    const dispatch = useDispatch();
    const loginError = useSelector(makeGetLoginError());
    const sessionState = useSelector(makeGetSessionState());
    const loggedIn = useSelector(makeGetLoggedInState());
    const loggedInGuest = useSelector(makeGetLoggedInReturnGuestState());
    const resetPasswordState = useSelector(makeGetResetPasswordState());
    const guestError = useSelector(makeGetErrorReturnGuestState());
    const loading = useSelector(makeGetLoginLoading());
    const loadingGuest = useSelector(makeGetLoginReturnGuestLoading());
    const cookieConsentSettingAvailable = useSelector(makeGetCookieConsentSettingAvailable());
    const [shouldRender, setShouldRender] = useState(false);
    const { t } = useTranslation([I18N_NAMESPACES.navigation, I18N_NAMESPACES.account]);
    useEffect(() => {
        dispatch(authActions.createInitSocialAction());
    }, []);
    useEffect(() => {
        (sessionState.authorities || sessionState.error) && !shouldRender && setShouldRender(true);
    }, [sessionState, shouldRender]);
    useEffect(() => {
        if (loggedIn) {
            dispatch(analyticsActions.returnPortalLogin(ReturnPortalLoginType.KNOWN_USER));
            window.location.replace(getHomepageUrl(returnItemsPath));
        }
    }, [loggedIn]);
    useEffect(() => {
        if (loggedInGuest && !loggedIn) {
            dispatch(analyticsActions.returnPortalLogin(ReturnPortalLoginType.GUEST));
            window.location.replace(getHomepageUrl(orderOverviewPath));
        }
    }, [loggedInGuest, loggedIn]);
    useEffect(() => {
        if (loginError && !loading) {
            ScrollUtil.scrollToErrorElement(document.getElementById(LOGIN_FORM_ERROR_FEEDBACK_ID));
        }
    }, [loginError, loading]);
    useEffect(() => {
        if (guestError && !loadingGuest) {
            ScrollUtil.scrollToErrorElement(document.getElementById(RETURN_PORTAL_GUEST_FORM_ERROR_FEEDBACK_ID));
        }
    }, [guestError, loadingGuest]);
    if (!shouldRender || loggedIn)
        return null;
    const handleLoginSubmit = (values) => {
        dispatch(authActions.login({
            username: values.email,
            password: values.password,
            keepSignedIn: values.keepSignedIn,
            reloadOnSuccess: false,
            location: AuthFormLocation.RETURN_PORTAL,
        }));
    };
    const handleResetPasswordSubmit = (values) => {
        dispatch(authActions.createResetCredentialsAction(values.email));
    };
    const handleSubmitGuest = (values) => {
        dispatch(authActions.createLoginReturnGuestAction({
            username: values.guestEmail,
            orderNumber: values.ordernumber,
        }));
    };
    const handleLoginRegistrationSuccess = (registrationEmail) => {
        dispatch(analyticsActions.returnPortalLogin(ReturnPortalLoginType.KNOWN_USER));
        window.location.href = `${getHomepageUrl(accountRegistrationPath)}?${RegistrationSearchParameters.EMAIL}=${registrationEmail}&${RegistrationSearchParameters.CALLBACK_URL}=${encodeURIComponent(getHomepageUrl(returnItemsPath))}`;
    };
    const resetForgotPasswordState = () => {
        (resetPasswordState.success || resetPasswordState.error) &&
            dispatch(authActions.createResetCredentialsSuccessAction(null));
    };
    const renderRegistration = enableLoginForm ? (_jsxs(Box, { marginInline: { base: '-4', md: '0' }, paddingInline: { base: '4', md: '0' }, children: [_jsx(LoginRegistrationForm, Object.assign({}, loginFormProps, { onSuccess: handleLoginRegistrationSuccess, emailValidationErrorMessages: {
                    invalidEmail: t(ErrorMessage.INVALID_EMAIL),
                    [AuthStatusCode.VALID_ACCOUNT]: t(ErrorMessage.RETURN_PORTAL_REGISTRATION_USER_KNOWN),
                    [AuthStatusCode.INVALID_ACCOUNT]: t(ErrorMessage.RETURN_PORTAL_REGISTRATION_USER_KNOWN_SAME_FASCIA_NO_PASSWORD),
                    [AuthStatusCode.VALID_ACCOUNT_OTHER_FASCIA]: t(ErrorMessage.RETURN_PORTAL_REGISTRATION_USER_KNOWN_OTHER_FASCIA),
                    [AuthStatusCode.INVALID_ACCOUNT_OTHER_FASCIA]: t(ErrorMessage.RETURN_PORTAL_REGISTRATION_USER_KNOWN_OTHER_FASCIA_NO_PASSWORD),
                } })), _jsx(Box, { paddingBlock: { base: '8', md: '6' }, children: _jsx(Divider, {}) })] })) : null;
    return (_jsxs(Box, { paddingBlockStart: { base: '6', md: '18' }, paddingBlockEnd: { base: '6', md: '14' }, children: [_jsx(Box, { paddingBlockEnd: { base: '6', md: '7', lg: '8' }, paddingInlineStart: { md: '12' }, children: _jsx(TextAlpha, { as: "h2", variant: "headingS", alignment: "start", children: t('account:account.return.login.title') }) }), _jsx(Container, { type: isMobile ? ContainerType.FULL_WIDTH : ContainerType.FLUID, children: _jsxs(Row, { children: [_jsx(Column, { mobile: 12, mobileLarge: 12, tabletSmall: 12, grid: 0, tablet: 6, desktop: 8, gutter: isMobile, children: _jsx(Box, { paddingInlineStart: { md: '12' }, paddingInlineEnd: { base: '0', md: '2', lg: '12' }, paddingBlockEnd: "6", children: _jsx(Authentication, { login: {
                                        onSubmit: handleLoginSubmit,
                                        errorFeedback: loginError,
                                        loading,
                                    }, forgotPassword: {
                                        onSubmit: handleResetPasswordSubmit,
                                        errorFeedback: resetPasswordState.error,
                                        successFeedback: resetPasswordState.success,
                                    }, facebook: {}, cookieConsentSettingAvailable: cookieConsentSettingAvailable === CookieConsentSettingState.CONSENTS_AVAILABLE, socialLogin: payload => dispatch(authActions.createSocialLoginAction(payload)), onAuthResetForm: () => dispatch(authActions.resetError()), resetForgotPasswordState: resetForgotPasswordState }) }) }), _jsx(Column, { mobile: 12, mobileLarge: 12, tabletSmall: 12, grid: 0, tablet: 6, desktop: 4, gutter: isMobile, children: _jsxs(Box, { marginInline: { base: '-4', md: '0' }, paddingBlockStart: "2", paddingInlineStart: { base: '4', md: '8' }, paddingInlineEnd: { base: '4', md: '16' }, children: [renderRegistration, _jsx(ReturnPortalGuestLogin, { onSubmit: handleSubmitGuest, errorFeedback: guestError ? t(guestError) : undefined, loading: loadingGuest }), isMobile && _jsx(Divider, {})] }) })] }) })] }));
};
export default ReturnPortalLoginContainer;
